// Copie en mémoire d'un fichier choisi par le membre, octet pour octet.
//
// POURQUOI. Chrome sur Android relit la date du fichier au moment de l'envoi et la compare à celle
// lue à la sélection. Quand la galerie du téléphone n'est pas encore à jour (vidéo tout juste
// filmée, photo retouchée ou exportée), les deux diffèrent : Chrome refuse d'envoyer avant le
// premier octet (`ERR_UPLOAD_FILE_CHANGED`, vu par axios comme `ERR_NETWORK`), et chaque nouvel
// essai sur le même fichier échoue de la même façon. Première cause de publications perdues le
// 12/10/2026 (13 fichiers sur 27). Une copie en mémoire n'a plus de fichier derrière elle : le
// contrôle ne s'applique pas.
//
// La copie est identique à l'original, métadonnées comprises (EXIF, XMP, marqueurs « contenu
// IA ») : rien n'est décodé ni réencodé, contrairement à l'éditeur de photo.
//
// Lecture par morceaux : chaque morceau est versé dans un Blob dont le navigateur garde les
// données hors de la page (et les range sur le disque au besoin). La page ne tient jamais plus
// d'un morceau à la fois, même pour une vidéo de 1 Go.

export const FILE_COPY_ERROR = {
    TOO_LARGE: 'too_large',
    NO_SPACE: 'no_space',
    UNREADABLE: 'unreadable',
    // Vidéo dont pas un octet n'a pu être lu : le téléphone ne la détient plus réellement (souvent
    // déplacée dans le cloud pour libérer de la place). Posé par useUploader, pour le message.
    VIDEO_UNAVAILABLE: 'video_unavailable',
    UNKNOWN: 'unknown',
};

// Limite de l'API pour une vidéo (routes/medias.js) : au-delà, l'envoi serait refusé de toute façon.
export const MAX_VIDEO_COPY_BYTES = 1024 * 1024 * 1024;

const CHUNK_BYTES = 8 * 1024 * 1024;

export class FileCopyError extends Error {
    /**
     * @param {string} reason FILE_COPY_ERROR.*
     * @param {*} [cause] erreur d'origine du navigateur
     * @param {Object} [details] où en était la copie : position, octets copiés, durée — de quoi
     *   distinguer une lecture refusée d'emblée d'une lecture qui lâche en route (diagnostic)
     */
    constructor(reason, cause = null, details = {}) {
        super(`Copie du fichier impossible (${reason})`);
        this.name = 'FileCopyError';
        this.reason = reason;
        this.cause = cause;
        this.details = details;
    }
}

// Ce que le navigateur remonte quand la copie échoue, ramené à ce que le membre peut y faire.
const classifyError = (error) => {
    const name = error?.name;
    // Espace de stockage du navigateur épuisé, ou mémoire refusée pour un morceau.
    if (name === 'QuotaExceededError' || error instanceof RangeError) return FILE_COPY_ERROR.NO_SPACE;
    // Le téléphone ne laisse plus lire le fichier (fournisseur de la galerie, fichier déplacé).
    if (name === 'NotReadableError' || name === 'NotFoundError' || name === 'SecurityError') return FILE_COPY_ERROR.UNREADABLE;
    return FILE_COPY_ERROR.UNKNOWN;
};

/**
 * @param {File} file
 * @param {Object} [options]
 * @param {number} [options.maxBytes] taille au-delà de laquelle on ne tente pas la copie
 * @returns {Promise<File>} une copie marquée `__isLocalCopy`, mêmes nom, type et date
 * @throws {FileCopyError}
 */
export const copyFile = async (file, { maxBytes = Infinity } = {}) => {
    if (!file) throw new FileCopyError(FILE_COPY_ERROR.UNKNOWN);
    if (file.size > maxBytes) throw new FileCopyError(FILE_COPY_ERROR.TOO_LARGE);

    const startedAt = Date.now();
    let copy = new Blob([], { type: file.type });
    let offset = 0;
    const details = () => ({ offset, octetsCopies: copy.size, dureeMs: Date.now() - startedAt });
    try {
        for (; offset < file.size; offset += CHUNK_BYTES) {
            const chunk = await file.slice(offset, offset + CHUNK_BYTES).arrayBuffer();
            copy = new Blob([copy, chunk], { type: file.type });
        }
    } catch (error) {
        throw new FileCopyError(classifyError(error), error, details());
    }
    // Une lecture tronquée sans erreur donnerait un fichier plus court que l'original.
    if (copy.size !== file.size) {
        throw new FileCopyError(FILE_COPY_ERROR.UNREADABLE, null, { ...details(), tailleIncoherente: true });
    }

    const result = new File([copy], file.name, { type: file.type, lastModified: file.lastModified });
    result.__isLocalCopy = true;
    // Le fichier d'origine : le diagnostic d'envoi y a déposé ses mesures prises à la sélection.
    result.__copiedFrom = file;
    return result;
};

export default copyFile;
